import { cn } from "@sylviq/ui-react-web/lib/cn";
import {
  type HTMLAttributes,
  type ReactNode,
  useLayoutEffect,
  useRef,
} from "react";

export interface AppShellTopInsetProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export interface AppShellBannerProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/** Neutral, token-colored banner content for the top inset. */
export function AppShellBanner({
  className,
  children,
  ...props
}: AppShellBannerProps) {
  return (
    <div className={cn("app-shell-banner", className)} role="status" {...props}>
      {children}
    </div>
  );
}

/**
 * A normal-flow banner that tells the fixed app shell how much vertical room
 * it currently occupies. The inset follows the banner while the page scrolls
 * so the floating header and sidebar settle at the viewport top as it passes.
 */
export function AppShellTopInset({
  className,
  children,
  ...props
}: AppShellTopInsetProps) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const root = document.documentElement;
    const previous = root.style.getPropertyValue("--app-shell-top-inset");
    const update = () => {
      const visibleBottom = Math.max(0, element.getBoundingClientRect().bottom);
      root.style.setProperty(
        "--app-shell-top-inset",
        `${visibleBottom}px`,
      );
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(element);

    return () => {
      window.removeEventListener("scroll", update);
      observer?.disconnect();
      if (previous) root.style.setProperty("--app-shell-top-inset", previous);
      else root.style.removeProperty("--app-shell-top-inset");
    };
  }, []);

  return (
    <div
      ref={ref}
      className={cn("app-shell-top-inset", className)}
      {...props}
    >
      {children}
    </div>
  );
}
